import { useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useIsModuleEnabled } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { useHomeTaskCounts } from './useHomeTaskCounts';
import { TASK_DEFINITIONS } from '../config/task-definitions';
import type { Task, TaskUrgency, StaticTask } from '../types/home-tasks.types';
import { URGENCY_ORDER } from '../types/home-tasks.types';

/**
 * Static placeholder tasks (for demo purposes)
 * Keep in sync with TaskList.tsx
 */
const STATIC_TASKS: StaticTask[] = [
  {
    id: 'static-inventory-microchip',
    title: 'Restock materials for Microchip demand',
    urgency: 'high',
    done: false,
    targetPath: '/procurement-demo/inventory',
  },
  {
    id: 'static-1',
    title: 'Review AP Invoice #4521',
    urgency: 'urgent',
    done: false,
  },
  {
    id: 'static-2',
    title: 'Approve PO for Q2 supplies',
    urgency: 'high',
    done: false,
  },
  {
    id: 'static-3',
    title: 'Update rebate agreement terms',
    urgency: 'high',
    done: true,
  },
  {
    id: 'static-4',
    title: 'Process inbound shipment',
    urgency: 'normal',
    done: false,
  },
  {
    id: 'static-5',
    title: 'Check vendor payment status',
    urgency: 'normal',
    done: false,
  },
  {
    id: 'static-6',
    title: 'Archive completed orders',
    urgency: 'low',
    done: true,
  },
  {
    id: 'static-rebate-1',
    title: 'Review Lumina - Rebate Agreement 2026',
    urgency: 'urgent',
    done: false,
    targetPath: '/rebates/agreements',
  },
  {
    id: 'static-rebate-2',
    title: 'Process rebate collection for NexiPoint Logistics',
    urgency: 'high',
    done: false,
    targetPath: '/rebates/debtors',
  },
  {
    id: 'static-rebate-3',
    title: 'Verify forecast calculations for Stratos Manufacturing',
    urgency: 'high',
    done: false,
    targetPath: '/rebates/analytics',
  },
  {
    id: 'static-rebate-4',
    title: 'Review debtors report for Lumina Q4 2025',
    urgency: 'normal',
    done: false,
    targetPath: '/rebates/debtors',
  },
  {
    id: 'static-rebate-5',
    title: 'Finalize Aetheria Solutions - Rebate Agreement 2026',
    urgency: 'normal',
    done: false,
    targetPath: '/rebates/agreements',
  },
  {
    id: 'static-rebate-6',
    title: 'Export rebate data for month-end reconciliation',
    urgency: 'low',
    done: false,
    targetPath: '/rebates/data-export',
  },
];

export interface UseHomeTasksReturn {
  /** List of tasks with counts > 0, sorted by urgency */
  tasks: Task[];
  /** Whether task counts are loading */
  isLoading: boolean;
  /** Error if task counts failed to load */
  error: Error | null;
  /** Handler to navigate to a task's target page */
  handleTaskClick: (task: Task) => void;
  /** Total pending task count (dynamic + static) */
  totalCount: number;
  /** Count of high priority tasks (urgent + high) */
  highPriorityCount: number;
}

/**
 * Main hook for home page tasks
 *
 * Combines task definitions with counts from the backend,
 * filters by module access, and provides navigation handler
 */
export function useHomeTasks(): UseHomeTasksReturn {
  const { isModuleEnabled, isLoading: isModulesLoading } = useIsModuleEnabled();
  const { getCount, isLoading: isCountsLoading, error } = useHomeTaskCounts();
  const navigate = useNavigate();

  // Build tasks array from definitions + counts
  const tasks = useMemo(() => {
    const result: Task[] = [];

    for (const definition of TASK_DEFINITIONS) {
      // Skip if user doesn't have required module
      if (
        definition.requiredModule &&
        !isModuleEnabled(definition.requiredModule)
      ) {
        continue;
      }

      const count = getCount(definition.id);

      // Skip tasks with count 0 (hide them)
      if (count === 0) {
        continue;
      }

      result.push({
        id: definition.id,
        title: definition.getTitle(count),
        urgency: definition.urgency,
        count,
        targetPath: definition.targetPath,
        searchParams: definition.searchParams,
      });
    }

    // Sort by urgency
    result.sort((a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency]);

    return result;
  }, [getCount, isModuleEnabled]);

  // Navigation handler
  const handleTaskClick = useCallback(
    (task: Task) => {
      navigate({
        to: task.targetPath,
        search: task.searchParams,
      });
    },
    [navigate],
  );

  // Static task counts (pending only)
  const staticPendingCount = STATIC_TASKS.filter((t) => !t.done).length;
  const staticHighPriorityCount = STATIC_TASKS.filter(
    (t) => !t.done && (t.urgency === 'urgent' || t.urgency === 'high'),
  ).length;

  // Compute totals (dynamic + static)
  const totalCount = useMemo(
    () => tasks.reduce((sum, task) => sum + task.count, 0) + staticPendingCount,
    [tasks, staticPendingCount],
  );

  const highPriorityCount = useMemo(
    () =>
      tasks
        .filter((t) => t.urgency === 'urgent' || t.urgency === 'high')
        .reduce((sum, task) => sum + task.count, 0) + staticHighPriorityCount,
    [tasks, staticHighPriorityCount],
  );

  return {
    tasks,
    isLoading: isModulesLoading || isCountsLoading,
    error: error as Error | null,
    handleTaskClick,
    totalCount,
    highPriorityCount,
  };
}
